import {
  Box,
  SubTitle,
  Table,
  Pagination,
  formattedCurrency,
  usePagination,
  useUpdateQueryParams,
  MaterialSymbol,
  Tooltip,
  Timestamp,
  Timezone,
  TableProps,
  capitalizeFirstLetter,
  TransactionTypesOptions,
  TransactionsLists,
  TransactionHistoryStatus,
  SubAccountTransactionsHistoryRequest,
} from 'vopay';
import { FunctionComponent, useEffect, useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { subWeeks } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import queryString from 'query-string';
import useFileDownload from '@/hooks/useFiledDownload';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import TransactionSearchForm from './TransactionSearchForm';
import styles from './Transactions.module.scss';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useTranslation } from 'react-i18next';

import useFetchSubAccountTransactions from '@/hooks/useApi/useFetchSubAccountTransactions';

import { CopyButton, TableFiltersContainer, Truncate } from '@/components/UI';
import TransactionsExport from './TransactionsExport';
import TransactionStatus from '@/components/UI/TransactionStatus/TransactionStatus';
import moment from 'moment';

type QueryParams = {
  type: TransactionTypesOptions;
  status: TransactionHistoryStatus;
  flagged: string;
  searchTerm: string;
  timeZone: string;
  startDate: string;
  endDate: string;
};

type ScheduledTransactionSearchParams = {
  type?: TransactionTypesOptions;
  status?: TransactionHistoryStatus;
  flagged?: string;
  searchTerm?: string;
  timeZone?: string;
  timeRange?: [Timestamp, Timestamp];
};

type TransactionsHistoryProps = {
  accountId: string;
  subAccountType: string;
  currency: string;
};

type FC = FunctionComponent<TransactionsHistoryProps>;

const TransactionsHistory: FC = (props: TransactionsHistoryProps) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Transaction.TransactionHistory.TransactionHistory' });
  const { t: tGlobal } = useTranslation('translations');
  const { accountId, subAccountType, currency } = props;
  const { downloadPdf, downloadCsv, downloadJson, downloadXls, downloadDoc, downloadTxt, downloadXml, printFile } =
    useFileDownload();
  const [filteredList, setFilteredList] = useState<TransactionsLists[]>([]);
  const [transactionsHistoryExport, setTransactionsHistoryExport] = useState<TransactionsLists[]>([]);
  const location = useLocation();
  const { userSettings } = useUserSettingsProvider();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const type = useMemo<TransactionTypesOptions>(
    () => (queryParams.get('type') as TransactionTypesOptions) || '',
    [queryParams],
  );
  const status = useMemo<TransactionHistoryStatus>(
    () => (queryParams.get('status') as TransactionHistoryStatus) || null,
    [queryParams],
  );
  const flagged = useMemo(() => queryParams.get('flagged') || '', [queryParams]);
  const searchTerm = useMemo(() => queryParams.get('searchTerm') || '', [queryParams]);
  const timeZone = useMemo(
    () => (queryParams.get('timeZone') as Timezone) || userSettings.timezone || 'UTC',
    [queryParams],
  );
  const filtersApplied = useMemo(() => Object.keys(queryParams).length > 0, [queryParams]);

  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  const { transactions: transactionsResponse, fetchTransactions } = useFetchSubAccountTransactions();
  const { transactions: transactionFileResponse, fetchTransactions: getTransactionsFile } =
    useFetchSubAccountTransactions();
  const { fetchTransactions: getTransactionsHistoryExport } = useFetchSubAccountTransactions();

  const currentDateTime = useMemo(() => formatInTimeZone(new Date(), timeZone, 'yyyy-MM-dd'), [timeZone]);
  const timeDifference = useMemo(() => formatInTimeZone(subWeeks(new Date(), 1), timeZone, 'yyyy-MM-dd'), [timeZone]);

  const startDate = useMemo(() => queryParams.get('startDate') || timeDifference, [queryParams]);
  const endDate = useMemo(() => queryParams.get('endDate') || currentDateTime, [queryParams]);

  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false);
  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);

  const transactionsPeriod = {
    FromDate: startDate || timeDifference,
    ToDate: endDate || currentDateTime,
  };

  const alertDialog = useAlertDialog();

  // pagination
  const totalDataLength = useMemo(() => {
    return transactionsResponse?.PaginationMetaData?.TotalRecords || 0;
  }, [transactionsResponse?.Transactions]);
  const PER_PAGE = 20;
  const { pageCount, pageIndex, setPageIndex } = usePagination(totalDataLength, {
    pageSize: PER_PAGE,
  });

  const search = async (offset = 0, searchParams: ScheduledTransactionSearchParams = {}) => {
    const defaultValues = {
      StartDateTime: moment(searchParams.timeRange?.[0] ?? (startDate || timeDifference)).format('YYYY-MM-DD'),
      EndDateTime: moment(searchParams.timeRange?.[1] ?? (endDate || currentDateTime)).format('YYYY-MM-DD'),
      ...(searchParams.type === 'All' ? {} : { TransactionType: searchParams.type }),
      ...(searchParams.status === 'All' ? {} : { TransactionStatus: searchParams.status }),
      ...(searchParams.flagged === 'All' || searchParams.flagged === ''
        ? {}
        : searchParams.flagged === 'Yes'
          ? { IsFlagged: true }
          : { IsFlagged: false }),
      SearchText: searchParams.searchTerm ?? searchTerm,
      Timezone: searchParams.timeZone ?? timeZone,
      Currency: currency,
      Limit: PER_PAGE,
      Offset: offset,
    };
    if (hasInvalidEndDate) {
      setShouldShowEndDateError(true);
      return;
    }

    try {
      const res = await fetchTransactions({
        ...defaultValues,
        SubAccountID: accountId,
      });

      if (!res.Success) throw new Error(res.ErrorMessage);
      const subaccountTransactionRequest = Object.values(res.Transactions || {});
      setFilteredList(subaccountTransactionRequest);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'An error occurred while fetching eLinx history';
      alertDialog.error('Error', msg);
      console.error(err);
    }
  };

  useEffect(() => {
    if (startDate > endDate) {
      setHasInvalidEndDate(true);
      setShouldShowEndDateError(true);
    } else {
      setHasInvalidEndDate(false);
    }
  }, [endDate]);

  useEffect(() => {
    const offset = setPageIndex(0);
    const searchParams = {
      type,
      status,
      flagged,
      searchTerm,
      timeZone,
      startDate: moment(startDate || timeDifference).format('YYYY-MM-DD'),
      endDate: moment(endDate || currentDateTime).format('YYYY-MM-DD'),
    };
    updateQueryParams(searchParams);
    search(offset, searchParams);
  }, []);

  useEffect(() => {
    if (!transactionFileResponse) return;
    const { Success, CSVFile, PDFFile } = transactionFileResponse;

    if (Success) {
      if (PDFFile) {
        downloadPdf(PDFFile, 'transactions');
      } else if (CSVFile) {
        downloadCsv(CSVFile, 'transactions');
      }
    }
  }, [transactionFileResponse]);
  const handleExport = async (exportType: 'pdf' | 'csv' | 'xls' | 'doc' | 'text' | 'json' | 'xml' | 'print') => {
    const exportOptions = {
      SubAccountID: accountId,
      StartDateTime: startDate,
      EndDateTime: endDate,
      Timezone: timeZone,
      TransactionStatus: status === null || status === 'All' ? undefined : status,
      IsFlagged: flagged === 'All' || flagged === '' ? undefined : flagged === 'Yes',
      TransactionType:
        type === 'All' || type === '' || type === 'Refund' || type === 'Auto Balance Transfer' ? undefined : type,
      Currency: currency,
      SearchText: searchTerm,
    };

    const handleExportWithFile = async (
      options: SubAccountTransactionsHistoryRequest,
      exportFn: (id: string, filename: string) => void,
    ) => {
      const resExport = await getTransactionsHistoryExport(options);
      setTransactionsHistoryExport(Object.values(resExport.Transactions || {}));
      exportFn('transactionHistoryTableExport', 'transactions');
    };

    const exportActions: Record<string, () => void> = {
      pdf: () => getTransactionsFile({ ...exportOptions, ExportPDF: true }),
      csv: () => getTransactionsFile({ ...exportOptions, ExportCSV: true }),
      xls: () => handleExportWithFile(exportOptions, downloadXls),
      doc: () => handleExportWithFile(exportOptions, downloadDoc),
      text: () => handleExportWithFile(exportOptions, downloadTxt),
      json: async () => {
        const exportTransactionData = await getTransactionsHistoryExport(exportOptions);
        if (exportTransactionData) downloadJson({ Transactions: exportTransactionData.Transactions }, 'transactions');
      },
      xml: () => handleExportWithFile(exportOptions, downloadXml),
      print: async () => {
        const res = await getTransactionsHistoryExport(exportOptions);
        setTransactionsHistoryExport(Object.values(res.Transactions || {}));
        printFile('transactionHistoryTableExport');
      },
    };

    if (exportActions[exportType]) {
      await exportActions[exportType]();
    }
  };

  // search form
  useEffect(() => {
    setFilteredList(Object.values(transactionsResponse?.Transactions || {}));
  }, [transactionsResponse?.Transactions]);

  const tableProps: TableProps<TransactionsLists> = {
    id: 'transactionHistoryTable',

    version: '2',
    zebra: true,
    columns: [
      {
        id: 'transactionDate',
        header: t('table.transactionDate'),
        align: 'center',

        style: {
          whiteSpace: 'nowrap',
          minWidth: '124px',
        },
        template: (transaction) => {
          return (
            <Box gap="sm" ai="center">
              {transaction.data.TransactionFlag !== '' && (
                <Tooltip
                  information={transaction.data.TransactionFlag}
                  hasButton={false}
                  customElement={<MaterialSymbol fillAmount={1} icon="flag" size="24px" color="rgba(189, 58, 45, 1)" />}
                  position="right"
                />
              )}
              <span>{transaction.data.TransactionDateTime}</span>
            </Box>
          );
        },
      },
      {
        id: 'transId',
        header: t('table.id'),
        align: 'center',
        style: {
          minWidth: '124px',
        },

        template: ({ data }) => {
          return (
            <Box gap="sm" ai="center">
              <Link
                className={styles.transactionIDLink}
                to={{
                  pathname: `/sub-account-transaction/details/${subAccountType}/${data.TransactionID}/${
                    data.Currency
                  }?${queryString.stringify(data)}`,
                  state: {
                    transaction: data,
                    transactionsPeriod,
                    type,
                    id: data.TransactionID,
                  },
                }}
              >
                {data.TransactionID}
              </Link>
              <CopyButton content={data.TransactionID} color="rgba(45, 45, 45, 0.6)" />
            </Box>
          );
        },
      },
      {
        id: 'name',
        header: t('table.name'),
        align: 'center',
        field: 'FullName',
        style: {
          minWidth: '124px',
        },
      },
      {
        id: 'type',
        header: t('table.type'),
        align: 'center',
        template: (transaction) => {
          return tGlobal(`Transaction.TransactionHistory.transactionTypes.${transaction.data.TransactionType}`, {
            defaultValue: transaction.data.TransactionType,
          });
        },
        style: {
          minWidth: '124px',
        },
      },
      {
        id: 'referenceNumber',
        header: t('table.referenceNumber'),
        align: 'center',
        field: 'ClientReferenceNumber',
        style: {
          minWidth: '124px',
        },
      },
      {
        id: 'credit',
        header: t('table.credit'),
        align: 'center',
        style: {
          whiteSpace: 'nowrap',
          minWidth: '124px',
        },
        template: (transaction) => {
          return (
            <>
              {transaction.data.CreditAmount !== '0.00' && (
                <Box flx="col" gapSize="6px">
                  <span>{`$${formattedCurrency(transaction.data.CreditAmount)}`}</span>
                  {transaction.data.HoldAmount !== '0.00' && (
                    <span className={styles.pendingAmount}>
                      {t('table.pending')} {`$${formattedCurrency(transaction.data.HoldAmount)}`}
                    </span>
                  )}
                </Box>
              )}
            </>
          );
        },
      },
      {
        id: 'debit',
        header: t('table.debit'),
        align: 'center',
        style: {
          whiteSpace: 'nowrap',
          minWidth: '124px',
        },
        template: (transaction) => {
          return (
            <>
              {transaction.data.DebitAmount !== '0.00' && (
                <Box flx="col" gapSize="6px">
                  <span>{`$${formattedCurrency(transaction.data.DebitAmount)}`}</span>
                  {transaction.data.HoldAmount !== '0.00' && (
                    <span className={styles.pendingAmount}>
                      {t('table.pending')} {`$${formattedCurrency(transaction.data.HoldAmount)}`}
                    </span>
                  )}
                </Box>
              )}
            </>
          );
        },
      },
      {
        id: 'status',
        header: t('table.status'),
        align: 'center',
        style: {
          whiteSpace: 'nowrap',
          minWidth: '124px',
        },
        template: (transaction) => {
          return (
            <Box gapSize="10px">
              <TransactionStatus
                status={transaction.data.TransactionStatus}
                label={
                  capitalizeFirstLetter(
                    tGlobal(`Transaction.TransactionHistory.statuses.${transaction.data.TransactionStatus}`, {
                      defaultValue: transaction.data.TransactionStatus,
                    }),
                  ) || ''
                }
              />
              {transaction.data.TransactionFailureReason && (
                <Tooltip
                  information={transaction.data.TransactionFailureReason}
                  icon="info"
                  variant="outlined"
                  iconWidth="14px"
                  buttonWidth="22px"
                  hasButton={true}
                  position="bottomLeft"
                />
              )}
            </Box>
          );
        },
      },
      {
        id: 'notes',
        header: t('table.notes'),
        align: 'center',
        style: {
          minWidth: '220px',
        },
        template: (transaction) => {
          const isTruncate = transaction.data.Notes.length > 25;

          return (
            <>
              <Tooltip
                information={transaction.data.Notes}
                isDisabled={!isTruncate}
                hasButton={false}
                customElement={
                  <Box gap="sm" ai="center" jc="sb" className="notes">
                    <Truncate text={transaction.data.Notes} maxLength={25} truncateEnding="..." />
                    {transaction.data.Notes && (
                      <CopyButton content={transaction.data.Notes} color="rgba(45, 45, 45, 0.6)" />
                    )}
                  </Box>
                }
                position="bottomLeft"
              />
            </>
          );
        },
      },
    ],
    rows:
      filteredList.map((transaction): { id: string; data: TransactionsLists } => ({
        id: transaction.TransactionID,
        data: transaction,
      })) || [],
  };
  const exportTableProps: TableProps = {
    id: 'transactionHistoryTableExport',
    name: 'transactions',
    columns: {
      id: { header: t('table.id'), align: 'left' },
      name: { header: t('table.name'), align: 'left' },
      transactionDate: { header: t('table.transactionDate'), align: 'left' },
      type: { header: t('table.type') || '', align: 'left' },
      referenceNumber: { header: t('table.referenceNumber'), align: 'left' },
      amount: { header: t('table.amount') },
      pending: { header: t('table.pending') },
      flag: { header: t('table.flag') },
      status: { header: t('table.status') },
      failureReason: { header: t('table.failureReason') },
      notes: { header: t('table.notes') },
    },
    rows:
      transactionsHistoryExport.map((transaction) => ({
        id: transaction.TransactionID,
        name: transaction.FullName,
        transactionDate: transaction.TransactionDateTime,
        type: tGlobal(`Transaction.TransactionHistory.transactionTypes.${transaction.TransactionType}`, {
          defaultValue: transaction.TransactionType,
        }),
        referenceNumber: transaction.ClientReferenceNumber,
        amount: `$${
          transaction.CreditAmount === '0.00'
            ? formattedCurrency(transaction.DebitAmount)
            : formattedCurrency(transaction.CreditAmount)
        }`,
        pending: `$${formattedCurrency(transaction.HoldAmount)}`,
        flag: (
          <div className={styles.flagSection}>
            <span>{transaction.TransactionFlag} </span>
          </div>
        ),
        status:
          capitalizeFirstLetter(
            tGlobal(`Transaction.TransactionHistory.statuses.${transaction.TransactionStatus}`, {
              defaultValue: transaction.TransactionStatus,
            }),
          ) || '',
        failureReason: transaction.TransactionFailureReason,
        notes: transaction.Notes,
      })) || [],
  };
  return (
    <Box flx="col">
      <Box jc="sb" ai="center" width="100%">
        <SubTitle subTitle={t('transactions.title')} />
        <TransactionsExport onExport={handleExport} />
      </Box>

      <TableFiltersContainer className={styles.searchContainer} filtersApplied={filtersApplied}>
        <TableFiltersContainer.Trigger />
        <TableFiltersContainer.Content>
          <TransactionSearchForm
            type={type}
            status={status}
            timeZone={timeZone}
            flagged={flagged}
            searchTerm={searchTerm}
            initialTimeRange={[
              moment(startDate || timeDifference).valueOf() as Timestamp,
              moment(endDate || currentDateTime).valueOf() as Timestamp,
            ]}
            hasInvalidEndDate={hasInvalidEndDate}
            shouldShowEndDateError={shouldShowEndDateError}
            setShouldShowEndDateError={setShouldShowEndDateError}
            onSearch={(searchParams) => {
              const offset = setPageIndex(0);
              updateQueryParams({
                type: searchParams.type || '',
                status: searchParams.status,
                flagged: searchParams.flagged || '',
                searchTerm: searchParams.searchTerm || '',
                timeZone: searchParams.timeZone || '',
                startDate: moment(searchParams.timeRange?.[0]).format('YYYY-MM-DD'),
                endDate: moment(searchParams.timeRange?.[1]).format('YYYY-MM-DD'),
              });
              search(offset, searchParams);
            }}
          />
        </TableFiltersContainer.Content>
      </TableFiltersContainer>

      <Box mt="lg">
        <div className={styles.tableContainer} id="transactionTable">
          <Table {...tableProps} />
        </div>
      </Box>
      <div className={styles.tableContainer} style={{ display: 'none' }} id="exportTransactionTable">
        <Table {...exportTableProps} />
      </div>
      <Box width="100%" jc="center" mt="lg">
        {pageCount > 1 && (
          <Pagination
            pageCount={pageCount}
            forcePageNumber={pageIndex}
            handlePageClick={(e) => {
              const offset = setPageIndex(e.selected);
              const searchParams = {
                type,
                status,
                flagged,
                searchTerm,
                timeZone,
                startDate: moment(startDate || timeDifference).format('YYYY-MM-DD'),
                endDate: moment(endDate || currentDateTime).format('YYYY-MM-DD'),
              };
              updateQueryParams(searchParams);
              search(offset, searchParams);
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default TransactionsHistory;
